import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, CheckCircle, AlertTriangle, Copy, Globe, Check, Minus, Eye, Camera, Lightbulb, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import app, { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, getDocs, doc, getDoc, updateDoc,
  setDoc, deleteDoc, onSnapshot, orderBy, where } from 'firebase/firestore';
import imageCompression from 'browser-image-compression';

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

const PLANS = {
  free: { name: '無料プラン', price: '¥0', color: '#888',
    features: { menuLimit: 10, categoryLimit: 0, languages: '日本語・英語', photo: false, dataHistory: '当日のみ' } },
  pro: { name: 'Pro', price: '¥980/月', color: '#2d6a4f',
    features: { menuLimit: Infinity, categoryLimit: Infinity, languages: '日・英・中・韓・他', photo: true, dataHistory: '週間' } },
  // 旧プラン名の互換性維持
  standard: { name: 'Pro', price: '¥980/月', color: '#2d6a4f',
    features: { menuLimit: Infinity, categoryLimit: Infinity, languages: '日・英・中・韓・他', photo: true, dataHistory: '週間' } },
  premium: { name: 'Pro', price: '¥980/月', color: '#2d6a4f',
    features: { menuLimit: Infinity, categoryLimit: Infinity, languages: '日・英・中・韓・他', photo: true, dataHistory: '週間' } },
};

function Dashboard() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const logoInputRef = useRef(null);
  const audioRef = useRef(null);
  const [storeName, setStoreName] = useState('');
  const [storeId, setStoreId] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [plan, setPlan] = useState('free');
  const [menus, setMenus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIdEditor, setShowIdEditor] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [newStoreId, setNewStoreId] = useState('');
  const [newStoreName, setNewStoreName] = useState('');
  const [idError, setIdError] = useState('');
  const [idSaving, setIdSaving] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const [calls, setCalls] = useState([]);
  const [staffPin, setStaffPin] = useState('');
  const [pinSaved, setPinSaved] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [managingPlan, setManagingPlan] = useState(false);
  const [weeklyOrders, setWeeklyOrders] = useState([]);
  const [weeklyLoading, setWeeklyLoading] = useState(true);
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [reportOrders, setReportOrders] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [printRequested, setPrintRequested] = useState(false);

  const isStandard = plan === 'standard' || plan === 'premium' || plan === 'pro';

  async function handleUpgrade() {
    setUpgrading(true);
    try {
      const functions = getFunctions(app, 'us-central1');
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckoutSession({
        successUrl: window.location.origin + '/dashboard?upgraded=1',
        cancelUrl: window.location.origin + '/dashboard',
      });
      window.location.href = result.data.url;
    } catch (err) {
      console.error(err);
      alert('エラー: ' + (err.message || err.code || JSON.stringify(err)));
      setUpgrading(false);
    }
  }

  async function handleManagePlan() {
    setManagingPlan(true);
    try {
      const functions = getFunctions(app, 'us-central1');
      const createPortalSession = httpsCallable(functions, 'createPortalSession');
      const result = await createPortalSession({
        returnUrl: window.location.origin + '/dashboard',
      });
      window.location.href = result.data.url;
    } catch (err) {
      console.error(err);
      alert('エラー: ' + (err.message || err.code || JSON.stringify(err)));
      setManagingPlan(false);
    }
  }

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (!currentUser || !isStandard) return;
    const q = query(
      collection(db, 'stores', currentUser.uid, 'calls'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const callData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const pending = callData.filter(c => c.status === 'pending');
      if (pending.length > calls.filter(c => c.status === 'pending').length) {
        playNotificationSound();
      }
      setCalls(callData);
    });
    return () => unsub();
  }, [currentUser, isStandard]);

  useEffect(() => {
    if (!currentUser || !isStandard) { setWeeklyLoading(false); return; }
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setHours(0, 0, 0, 0);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
    const q = query(
      collection(db, 'stores', currentUser.uid, 'orders'),
      where('status', '==', 'paid'),
      where('createdAt', '>=', sevenDaysAgo)
    );
    setWeeklyLoading(true);
    const unsub = onSnapshot(q, (snapshot) => {
      setWeeklyOrders(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setWeeklyLoading(false);
    }, () => setWeeklyLoading(false));
    return () => unsub();
  }, [currentUser, isStandard]);

  useEffect(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 6);
    const fmt = (d) => d.toISOString().slice(0, 10);
    setReportFrom(fmt(from));
    setReportTo(fmt(to));
  }, []);

  useEffect(() => {
    if (printRequested && reportOrders !== null) {
      window.print();
      setPrintRequested(false);
    }
  }, [printRequested, reportOrders]);

  function downloadCsv(filename, rows) {
    const csv = rows.map(row =>
      row.map(cell => {
        const s = String(cell ?? '');
        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
      }).join(',')
    ).join('\r\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function fetchReportOrders() {
    if (!reportFrom || !reportTo) return null;
    setReportError('');
    if (new Date(reportFrom) > new Date(reportTo)) {
      setReportError('開始日は終了日より前にしてください');
      return null;
    }
    setReportLoading(true);
    try {
      const fromDate = new Date(reportFrom + 'T00:00:00');
      const toDate = new Date(reportTo + 'T00:00:00');
      toDate.setDate(toDate.getDate() + 1);
      const q = query(
        collection(db, 'stores', currentUser.uid, 'orders'),
        where('status', '==', 'paid'),
        where('createdAt', '>=', fromDate),
        where('createdAt', '<', toDate)
      );
      const snap = await getDocs(q);
      const orders = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.createdAt?.toMillis() || 0) - (b.createdAt?.toMillis() || 0));
      setReportOrders(orders);
      setReportLoading(false);
      return orders;
    } catch (err) {
      console.error(err);
      setReportError('データの取得に失敗しました: ' + err.message);
      setReportLoading(false);
      return null;
    }
  }

  async function handleExportReportCsv() {
    const orders = await fetchReportOrders();
    if (!orders) return;
    const header = ['日時', 'テーブル', '商品明細', '合計金額', 'ステータス'];
    const rows = [header, ...orders.map(o => [
      o.createdAt?.toDate ? o.createdAt.toDate().toLocaleString('ja-JP') : '',
      o.tableName || '',
      (o.items || []).map(i => `${i.nameJa}×${i.quantity}`).join(' / '),
      o.totalPrice || 0,
      o.status === 'paid' ? '会計済み' : o.status,
    ])];
    const total = orders.reduce((sum, o) => sum + (o.totalPrice || 0), 0);
    rows.push([]);
    rows.push(['合計', '', '', total, `${orders.length}件`]);
    downloadCsv(`menusore_report_${reportFrom}_${reportTo}.csv`, rows);
  }

  async function handlePrintReport() {
    const orders = await fetchReportOrders();
    if (!orders) return;
    setPrintRequested(true);
  }

  function playNotificationSound() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
      osc.frequency.setValueAtTime(880, ctx.currentTime + 0.2);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.4);
    } catch {}
  }

  async function fetchData() {
    const storeDoc = await getDoc(doc(db, 'stores', currentUser.uid));
    if (storeDoc.exists()) {
      const data = storeDoc.data();
      setStoreName(data.storeName);
      setNewStoreName(data.storeName);
      setStoreId(data.storeId || currentUser.uid);
      setNewStoreId(data.storeId || '');
      setPlan(data.plan || 'free');
      setLogoUrl(data.logoUrl || '');
      setLogoPreview(data.logoUrl || '');
      setStaffPin(data.staffPin || '');;
    }
    const q = query(collection(db, 'stores', currentUser.uid, 'menus'));
    const snapshot = await getDocs(q);
    setMenus(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    const catsSnap = await getDocs(collection(db, 'stores', currentUser.uid, 'categories'));
    setCategories(catsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  async function handleLogout() { await logout(); navigate('/'); }

  async function handleLogoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const options = { maxSizeMB: 0.1, maxWidthOrHeight: 400, useWebWorker: true };
      const compressed = await imageCompression(file, options);
      const formData = new FormData();
      formData.append('file', compressed);
      formData.append('upload_preset', UPLOAD_PRESET);
      formData.append('folder', `menusore/logos/${currentUser.uid}`);
      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
        { method: 'POST', body: formData }
      );
      const data = await res.json();
      setLogoPreview(data.secure_url);
      setLogoUrl(data.secure_url);
    } catch { alert('ロゴのアップロードに失敗しました'); }
    setLogoUploading(false);
  }

  async function handleSaveProfile() {
    if (!newStoreName.trim()) { alert('店舗名を入力してください'); return; }
    setProfileSaving(true);
    await updateDoc(doc(db, 'stores', currentUser.uid), {
      storeName: newStoreName.trim().slice(0, 20),
      logoUrl: logoUrl || '',
    });
    setStoreName(newStoreName.trim().slice(0, 20));
    setShowProfileEditor(false);
    setProfileSaving(false);
    alert('店舗情報を保存しました！');
  }


  async function handleSavePin() {
    if (staffPin.length !== 4) { alert("4桁のPINを入力してください"); return; }
    await updateDoc(doc(db, "stores", currentUser.uid), { staffPin });
    setPinSaved(true);
    setTimeout(() => setPinSaved(false), 2000);
  }
  async function handleConfirmCall(callId) {
    await updateDoc(doc(db, 'stores', currentUser.uid, 'calls', callId), {
      status: 'confirmed',
    });
  }

  async function handleDoneCall(callId) {
    await updateDoc(doc(db, 'stores', currentUser.uid, 'calls', callId), {
      status: 'done',
    });
  }

  async function checkNewId(id) {
    if (!id) return;
    if (!/^[a-z0-9-]{6,30}$/.test(id)) { setIdError('6〜30文字の英小文字・数字・ハイフンのみ'); return; }
    if (id === storeId) { setIdError('現在のIDと同じです'); return; }
    const snap = await getDoc(doc(db, 'storeIds', id));
    snap.exists() ? setIdError('このIDはすでに使われています') : setIdError('OK: 使用可能です');
  }

  async function handleSaveId() {
    if (!newStoreId || !/^[a-z0-9-]{6,30}$/.test(newStoreId)) { setIdError('6〜30文字の英小文字・数字・ハイフンのみ'); return; }
    if (newStoreId === storeId) { setIdError('現在のIDと同じです'); return; }
    const snap = await getDoc(doc(db, 'storeIds', newStoreId));
    if (snap.exists()) { setIdError('このIDはすでに使われています'); return; }
    if (!window.confirm(`店舗IDを「${newStoreId}」に変更しますか？\n古いQRコードは使えなくなります`)) return;
    setIdSaving(true);
    await deleteDoc(doc(db, 'storeIds', storeId));
    await setDoc(doc(db, 'storeIds', newStoreId), { uid: currentUser.uid });
    await updateDoc(doc(db, 'stores', currentUser.uid), { storeId: newStoreId });
    setStoreId(newStoreId);
    setShowIdEditor(false);
    setIdError('');
    setIdSaving(false);
    alert('店舗IDを変更しました！新しいQRコードを印刷し直してください。');
  }

  async function handleResetSoldCount() {
    if (!window.confirm("本日の販売数をリセットしますか？")) return;
    const batch = [];
    for (const menu of menus) {
      batch.push(updateDoc(doc(db, "stores", currentUser.uid, "menus", menu.id), { soldCount: 0 }));
    }
    await Promise.all(batch);
    await fetchData();
    alert("リセットしました！");
  }
  // 週次分析：過去7日間の日別売上・注文数・トップ商品
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - (6 - i));
    return d;
  });
  const dayLabels = ['日', '月', '火', '水', '木', '金', '土'];
  const weeklyDaily = weekDays.map((d) => {
    const next = new Date(d);
    next.setDate(next.getDate() + 1);
    const dayOrders = weeklyOrders.filter((o) => {
      if (!o.createdAt || !o.createdAt.toDate) return false;
      const t = o.createdAt.toDate();
      return t >= d && t < next;
    });
    return {
      date: d,
      label: dayLabels[d.getDay()],
      revenue: dayOrders.reduce((sum, o) => sum + (o.totalPrice || 0), 0),
      orderCount: dayOrders.length,
    };
  });
  const weeklyRevenueTotal = weeklyDaily.reduce((sum, d) => sum + d.revenue, 0);
  const weeklyOrderCountTotal = weeklyDaily.reduce((sum, d) => sum + d.orderCount, 0);
  const weeklyMaxRevenue = Math.max(1, ...weeklyDaily.map((d) => d.revenue));
  const weeklyItemCounts = {};
  weeklyOrders.forEach((o) => {
    (o.items || []).forEach((item) => {
      const key = item.nameJa || item.menuId;
      weeklyItemCounts[key] = (weeklyItemCounts[key] || 0) + (item.quantity || 0);
    });
  });
  const weeklyTopItems = Object.entries(weeklyItemCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const currentPlan = PLANS[plan] || PLANS.free;
  const menuCount = menus.length;
  const topMenus = [...menus].sort((a, b) => (b.soldCount || 0) - (a.soldCount || 0)).slice(0, 5);
  const menuUrl = `${window.location.origin}/menu/${storeId}`;
  const staffUrl = `${window.location.origin}/${storeId}/staff`;
  const pendingCalls = calls.filter(c => c.status === 'pending');
  const confirmedCalls = calls.filter(c => c.status === 'confirmed');

  return (
    <div style={styles.container}>
      <nav style={styles.nav}>
        <h1 style={styles.logo}>MenuSore</h1>
        <div style={styles.navBtns}>
          {isStandard && pendingCalls.length > 0 && (
            <div style={styles.callBadge}>
              <Bell size={14} style={{display:'inline',verticalAlign:'middle',marginRight:4}} />{pendingCalls.length}件
            </div>
          )}
          <button onClick={() => navigate('/menu-editor')} style={styles.btnPrimary}>メニュー管理</button>
          <button onClick={handleLogout} style={styles.btnOutline}>ログアウト</button>
        </div>
      </nav>

      <div style={styles.content}>
        <h2 style={styles.heading}>ダッシュボード</h2>

        {/* 呼び出し通知 */}
        {isStandard && (pendingCalls.length > 0 || confirmedCalls.length > 0) && (
          <div style={styles.callSection}>
            <h3 style={styles.callTitle}>
              <Bell size={16} style={{display:'inline',verticalAlign:'middle',marginRight:6}} />呼び出し通知
              {pendingCalls.length > 0 && (
                <span style={styles.pendingBadge}>{pendingCalls.length}件対応中</span>
              )}
            </h3>

            {pendingCalls.map(call => (
              <div key={call.id} style={styles.callCard}>
                <div style={styles.callInfo}>
                  <p style={styles.callTable}>{call.tableName}</p>
                  <p style={styles.callStatus}><span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:'#d69e2e',marginRight:6}} />新着・未対応</p>
                </div>
                <div style={styles.callBtns}>
                  <button onClick={() => handleConfirmCall(call.id)} style={styles.btnConfirm}>
                    向かいます
                  </button>
                  <button onClick={() => handleDoneCall(call.id)} style={styles.btnDone}>
                    完了
                  </button>
                </div>
              </div>
            ))}

            {confirmedCalls.map(call => (
              <div key={call.id} style={{...styles.callCard, opacity: 0.7}}>
                <div style={styles.callInfo}>
                  <p style={styles.callTable}>{call.tableName}</p>
                  <p style={styles.callStatus}><span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:'#38a169',marginRight:6}} />対応中</p>
                </div>
                <button onClick={() => handleDoneCall(call.id)} style={styles.btnDone}>
                  完了
                </button>
              </div>
            ))}
          </div>
        )}

        {/* プランボックス */}
        <div style={styles.planBox}>
          <div style={styles.planTop}>
            <div style={styles.planLeft}>
              <span style={{...styles.planBadge, background: currentPlan.color}}>
                {currentPlan.name}
              </span>
              <span style={styles.planPrice}>{currentPlan.price}</span>
            </div>
            {plan === 'free' && (
              <button style={styles.btnUpgrade} onClick={handleUpgrade} disabled={upgrading}>
                {upgrading ? '処理中…' : 'アップグレード'}
              </button>
            )}
            {isStandard && (
              <button style={styles.btnOutline} onClick={handleManagePlan} disabled={managingPlan}>
                {managingPlan ? '処理中…' : 'プラン管理'}
              </button>
            )}
          </div>
          <div style={styles.planGrid}>
            <div style={styles.planItem}>
              <span style={styles.planLabel}>メニュー</span>
              <span style={styles.planVal}>{menuCount}品・無制限</span>
            </div>
            <div style={styles.planItem}>
              <span style={styles.planLabel}>言語</span>
              <span style={styles.planVal}>{currentPlan.features.languages}</span>
            </div>
            <div style={styles.planItem}>
              <span style={styles.planLabel}>カテゴリ</span>
              <span style={styles.planVal}>
                {isStandard
                  ? `${categories.length} / ${currentPlan.features.categoryLimit === Infinity ? '無制限' : currentPlan.features.categoryLimit}`
                  : '—'}
              </span>
            </div>
            <div style={styles.planItem}>
              <span style={styles.planLabel}>写真</span>
              <span style={styles.planVal}>{currentPlan.features.photo ? <Check size={15} color='#2d6a4f' strokeWidth={2.5} /> : <Minus size={15} color='#bbb' strokeWidth={2} />}</span>
            </div>
            <div style={styles.planItem}>
              <span style={styles.planLabel}>データ</span>
              <span style={styles.planVal}>{currentPlan.features.dataHistory}</span>
            </div>
          </div>
          {plan === 'free' && (
            <p style={styles.upgradeHint}>
              <Lightbulb size={14} style={{display:"inline",verticalAlign:"middle",marginRight:4}} />アップグレードで中国語・韓国語、写真、カテゴリ、店員呼び出しが使えます
            </p>
          )}
        </div>

        {/* 統計カード */}
        <div style={styles.cards}>
          <div style={styles.card}>
            <p style={styles.cardLabel}>メニュー数</p>
            <p style={styles.cardValue}>{menuCount}<span style={styles.cardUnit}>品</span></p>
          </div>
          <div style={styles.card}>
            <p style={styles.cardLabel}>本日の販売</p>
            <p style={styles.cardValue}>
              {menus.reduce((sum, m) => sum + (m.soldCount || 0), 0)}
              <span style={styles.cardUnit}>食</span>
            </p>
          </div>
          <div style={styles.card}>
            <p style={styles.cardLabel}>売切</p>
            <p style={styles.cardValue}>
              {menus.filter(m => m.status === 'soldout').length}
              <span style={styles.cardUnit}>品</span>
            </p>
          </div>
        </div>

        {/* 売上ランキング */}
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", margin:"24px 0 12px"}}>
          <h3 style={{...styles.subHeading, margin:0}}>本日の売上ランキング</h3>
          <button onClick={handleResetSoldCount} style={{padding:"6px 12px", background:"#fff", border:"1.5px solid #e63946", color:"#e63946", borderRadius:"8px", cursor:"pointer", fontSize:"12px"}}>
            <RefreshCw size={14} style={{display:"inline",verticalAlign:"middle",marginRight:4}} />販売数リセット
          </button>
        </div>
        {loading ? <p style={styles.loadingText}>読み込み中...</p> : (
          <div style={styles.ranking}>
            {topMenus.length === 0 ? (
              <p style={styles.empty}>メニューを追加すると表示されます</p>
            ) : topMenus.map((menu, i) => (
              <div key={menu.id} style={styles.rankItem}>
                <span style={{
                  ...styles.rankNum,
                  background: i === 0 ? '#f4a261' : i === 1 ? '#aaa' : '#ccc'
                }}>{i + 1}</span>
                <span style={styles.rankName}>{menu.nameJa}</span>
                <span style={styles.rankCount}>{menu.soldCount || 0}食</span>
              </div>
            ))}
          </div>
        )}

        {/* 週次分析（Pro限定） */}
        {isStandard && (
          <>
            <h3 style={{...styles.subHeading, margin:"24px 0 12px"}}>週次分析</h3>
            {weeklyLoading ? <p style={styles.loadingText}>読み込み中...</p> : (
              <div style={styles.ranking}>
                <div style={{display:"flex", gap:12, marginBottom:16}}>
                  <div style={{...styles.card, flex:1}}>
                    <p style={styles.cardLabel}>過去7日間の売上</p>
                    <p style={styles.cardValue}>¥{weeklyRevenueTotal.toLocaleString()}</p>
                  </div>
                  <div style={{...styles.card, flex:1}}>
                    <p style={styles.cardLabel}>過去7日間の注文数</p>
                    <p style={styles.cardValue}>{weeklyOrderCountTotal}<span style={styles.cardUnit}>件</span></p>
                  </div>
                </div>
                <div style={{display:"flex", alignItems:"flex-end", gap:8, height:120, padding:"0 4px 8px", borderBottom:"1px solid #eee"}}>
                  {weeklyDaily.map((d, i) => {
                    const heightPx = d.revenue === 0 ? 2 : Math.max(4, Math.round((d.revenue / weeklyMaxRevenue) * 96));
                    return (
                      <div key={i} style={{flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4}}>
                        <div
                          title={`${d.label}曜日: ¥${d.revenue.toLocaleString()}（${d.orderCount}件）`}
                          style={{
                            width:"100%", maxWidth:28, height:heightPx,
                            background: d.revenue > 0 ? "#667eea" : "#e2e8f0",
                            borderRadius:"4px 4px 0 0",
                            transition:"height 0.2s",
                          }}
                        />
                        <span style={{fontSize:11, color:"#a0aec0"}}>{d.label}</span>
                      </div>
                    );
                  })}
                </div>
                <h4 style={{...styles.subHeading, fontSize:14, margin:"20px 0 8px"}}>週間人気メニュー</h4>
                {weeklyTopItems.length === 0 ? (
                  <p style={styles.empty}>今週の注文データがまだありません</p>
                ) : weeklyTopItems.map(([name, count], i) => (
                  <div key={name} style={styles.rankItem}>
                    <span style={{
                      ...styles.rankNum,
                      background: i === 0 ? '#f4a261' : i === 1 ? '#aaa' : '#ccc'
                    }}>{i + 1}</span>
                    <span style={styles.rankName}>{name}</span>
                    <span style={styles.rankCount}>{count}食</span>
                  </div>
                ))}
              </div>
            )}

            {/* レポート出力（CSV/PDF） */}
            <h3 style={{...styles.subHeading, margin:"24px 0 12px"}}>レポート出力</h3>
            <div style={styles.ranking}>
              <div style={{display:"flex", flexWrap:"wrap", gap:8, alignItems:"center", marginBottom:12}}>
                <label style={{fontSize:13, color:"#4a5568"}}>
                  期間：
                  <input type="date" value={reportFrom} onChange={e => { setReportFrom(e.target.value); setReportOrders(null); }} style={{marginLeft:6, padding:"4px 6px"}} />
                </label>
                <span style={{color:"#a0aec0"}}>〜</span>
                <input type="date" value={reportTo} onChange={e => { setReportTo(e.target.value); setReportOrders(null); }} style={{padding:"4px 6px"}} />
              </div>
              <div style={{display:"flex", flexWrap:"wrap", gap:8}}>
                <button onClick={handleExportReportCsv} style={styles.btnOutline} disabled={reportLoading}>
                  {reportLoading ? '集計中...' : 'CSVダウンロード'}
                </button>
                <button onClick={handlePrintReport} style={styles.btnOutline} disabled={reportLoading}>
                  {reportLoading ? '集計中...' : 'レポートをPDFで保存（印刷）'}
                </button>
              </div>
              {reportError && <p style={{color:"#e53e3e", fontSize:13, marginTop:8}}>{reportError}</p>}
              {reportOrders !== null && (
                <p style={{fontSize:13, color:"#718096", marginTop:8}}>
                  {reportFrom}〜{reportTo}：{reportOrders.length}件、
                  合計¥{reportOrders.reduce((sum, o) => sum + (o.totalPrice || 0), 0).toLocaleString()}
                </p>
              )}
            </div>
          </>
        )}

        {/* 印刷専用エリア（レポートPDF化） */}
        <style>{`
          @media print {
            body * { visibility: hidden; }
            .print-report-area, .print-report-area * { visibility: visible; }
            .print-report-area { display: block !important; position: absolute; top: 0; left: 0; width: 100%; padding: 24px; }
          }
        `}</style>
        <div className="print-report-area" style={{display:"none"}}>
          <h2>{storeName} 売上レポート</h2>
          <p>期間：{reportFrom} 〜 {reportTo}</p>
          {reportOrders && (
            <>
              <p>
                件数：{reportOrders.length}件　合計：¥{reportOrders.reduce((sum, o) => sum + (o.totalPrice || 0), 0).toLocaleString()}
              </p>
              <table style={{width:"100%", borderCollapse:"collapse", marginTop:16}}>
                <thead>
                  <tr>
                    <th style={{border:"1px solid #ccc", padding:6, textAlign:"left"}}>日時</th>
                    <th style={{border:"1px solid #ccc", padding:6, textAlign:"left"}}>テーブル</th>
                    <th style={{border:"1px solid #ccc", padding:6, textAlign:"left"}}>商品明細</th>
                    <th style={{border:"1px solid #ccc", padding:6, textAlign:"right"}}>合計金額</th>
                  </tr>
                </thead>
                <tbody>
                  {reportOrders.map(o => (
                    <tr key={o.id}>
                      <td style={{border:"1px solid #ccc", padding:6}}>{o.createdAt?.toDate ? o.createdAt.toDate().toLocaleString('ja-JP') : ''}</td>
                      <td style={{border:"1px solid #ccc", padding:6}}>{o.tableName || ''}</td>
                      <td style={{border:"1px solid #ccc", padding:6}}>{(o.items || []).map(i => `${i.nameJa}×${i.quantity}`).join(' / ')}</td>
                      <td style={{border:"1px solid #ccc", padding:6, textAlign:"right"}}>¥{(o.totalPrice || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        {/* 店舗プロフィール */}
        <h3 style={styles.subHeading}>店舗プロフィール</h3>
        <div style={styles.settingBox}>
          <div style={styles.profileRow}>
            <div style={styles.logoWrap}>
              {logoPreview ? (
                <img src={logoPreview} alt="ロゴ" style={styles.logoImg} />
              ) : (
                <div style={styles.logoPlaceholder}>
                  <span style={styles.logoPlaceholderText}>
                    {storeName ? storeName.charAt(0) : '店'}
                  </span>
                </div>
              )}
            </div>
            <div style={styles.profileInfo}>
              <p style={styles.profileName}>{storeName}</p>
              <p style={styles.profileEmail}>{currentUser.email}</p>
            </div>
            <button onClick={() => setShowProfileEditor(!showProfileEditor)} style={styles.btnSmall}>
              {showProfileEditor ? 'キャンセル' : '編集'}
            </button>
          </div>

          {showProfileEditor && (
            <div style={styles.profileEditor}>
              <div style={styles.logoEditSection}>
                <p style={styles.editorLabel}>店舗ロゴ</p>
                <input type="file" accept="image/*" ref={logoInputRef}
                  onChange={handleLogoUpload} style={{ display: 'none' }} />
                <div style={styles.logoEditRow}>
                  {logoPreview ? (
                    <img src={logoPreview} alt="ロゴ" style={styles.logoPreview} />
                  ) : (
                    <div style={styles.logoPlaceholderSm}>
                      <span>{storeName ? storeName.charAt(0) : '店'}</span>
                    </div>
                  )}
                  <div style={styles.logoEditBtns}>
                    <button type="button" style={styles.btnPrimary}
                      onClick={() => logoInputRef.current && logoInputRef.current.click()}
                      disabled={logoUploading}>
                      {logoUploading ? 'アップロード中...' : <><Camera size={14} style={{display:'inline',verticalAlign:'middle',marginRight:4}} />ロゴを変更</>}
                    </button>
                    {logoPreview && (
                      <button type="button" style={styles.btnDanger}
                        onClick={() => { setLogoPreview(''); setLogoUrl(''); }}>
                        削除
                      </button>
                    )}
                  </div>
                </div>
                <p style={styles.hint}>推奨：正方形・100KB以下</p>
              </div>
              <div style={styles.nameEditSection}>
                <p style={styles.editorLabel}>店舗名</p>
                <input style={styles.input} value={newStoreName}
                  onChange={e => setNewStoreName(e.target.value.slice(0, 20))}
                  maxLength={20}
                  placeholder="店舗名を入力" />
                <p style={{ fontSize: 11, color: '#a0aec0', margin: '4px 0 0', textAlign: 'right' }}>
                  {newStoreName.length}/20文字
                </p>
              </div>
              <button onClick={handleSaveProfile} style={styles.btnPrimary}
                disabled={profileSaving || logoUploading}>
                {profileSaving ? '保存中...' : '保存する'}
              </button>
            </div>
          )}
        </div>

        {/* スタッフ設定 */}
        {isStandard && (
          <>
            <h3 style={styles.subHeading}>スタッフ設定</h3>
            <div style={styles.settingBox}>
              <div style={styles.settingRow}>
                <div style={{flex: 1, minWidth: 0}}>
                  <p style={styles.settingLabel}>スタッフ専用URL</p>
                  <p style={styles.settingValue}>{staffUrl}</p>
                </div>
                <div style={styles.settingBtns}>
                  <button onClick={() => { navigator.clipboard.writeText(staffUrl); alert("コピーしました"); }} style={styles.btnSmall}><Copy size={13} style={{display:'inline',verticalAlign:'middle',marginRight:3}} />コピー</button>
                  <button onClick={() => window.open(staffUrl, "_blank")} style={styles.btnSmall}><Eye size={13} style={{display:'inline',verticalAlign:'middle',marginRight:3}} />開く</button>
                </div>
              </div>
              <div style={{marginTop: 16, paddingTop: 16, borderTop: "1px solid #eee"}}>
                <p style={styles.settingLabel}>スタッフPIN（4桁）</p>
                <p style={{fontSize: 11, color: "#bbb", margin: "2px 0 8px"}}>スタッフ専用画面へのログインに使います</p>
                <div style={{display: "flex", gap: 8, alignItems: "center"}}>
                  <input type="password" inputMode="numeric" maxLength={4} value={staffPin} onChange={e => { const v = e.target.value.replace(/\D/g, "").slice(0,4); setStaffPin(v); setPinSaved(false); }} placeholder="0000" style={{width: 80, padding: 10, border: "1px solid #ddd", borderRadius: 8, fontSize: 20, textAlign: "center", letterSpacing: 6, boxSizing: "border-box"}} />
                  <button onClick={handleSavePin} disabled={staffPin.length !== 4} style={{...styles.btnPrimary, background: pinSaved ? "#38a169" : "#2d6a4f"}}>{pinSaved ? <><CheckCircle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:3}} />保存済み</> : '保存'}</button>
                </div>
                {!staffPin && <p style={{fontSize: 12, color: "#e53e3e", marginTop: 6}}><AlertTriangle size={13} style={{display:'inline',verticalAlign:'middle',marginRight:4}} />PINが未設定です</p>}
              </div>
            </div>
          </>
        )}

        {/* 店舗設定 */}
        <h3 style={styles.subHeading}>店舗設定</h3>
        <div style={styles.settingBox}>
          <div style={styles.settingRow}>
            <div style={{flex: 1, minWidth: 0}}>
              <p style={styles.settingLabel}>メニューURL</p>
              <p style={styles.settingValue}>{menuUrl}</p>
            </div>
            <div style={styles.settingBtns}>
              <button onClick={() => {
                navigator.clipboard.writeText(menuUrl);
                alert('URLをコピーしました！');
              }} style={styles.btnSmall}><Copy size={13} style={{display:'inline',verticalAlign:'middle',marginRight:3}} />コピー</button>
              <button onClick={() => window.open(menuUrl, '_blank')}
                style={styles.btnSmall}><Eye size={13} style={{display:'inline',verticalAlign:'middle',marginRight:3}} />プレビュー</button>
              <button onClick={() => setShowIdEditor(!showIdEditor)} style={styles.btnSmall}>
                {showIdEditor ? 'キャンセル' : 'ID変更'}
              </button>
            </div>
          </div>
          {showIdEditor && (
            <div style={styles.idEditor}>
              <p style={styles.warning}><AlertTriangle size={13} style={{display:'inline',verticalAlign:'middle',marginRight:4}} />変更すると古いQRコードが使えなくなります</p>
              <div style={styles.idRow}>
                <input style={styles.input} value={newStoreId}
                  onChange={e => { setNewStoreId(e.target.value.toLowerCase()); setIdError(''); }}
                  onBlur={e => checkNewId(e.target.value)}
                  placeholder="新しい店舗ID" />
                <button onClick={handleSaveId} style={styles.btnPrimary} disabled={idSaving}>
                  {idSaving ? '保存中' : '保存'}
                </button>
              </div>
              {idError && (
                <p style={{fontSize: '12px', marginTop: '6px',
                  color: idError.startsWith('OK') ? '#2d6a4f' : '#c00'}}>{idError}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#f8f9fa',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Hiragino Sans", sans-serif' },
  nav: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', background: '#fff', borderBottom: '1px solid #eee',
    position: 'sticky', top: 0, zIndex: 10 },
  logo: { margin: 0, color: '#2d6a4f', fontSize: '18px', fontWeight: '700' },
  navBtns: { display: 'flex', gap: '8px', alignItems: 'center' },
  callBadge: { background: '#f4a261', color: '#fff', fontSize: '12px', fontWeight: '700',
    padding: '4px 10px', borderRadius: '20px', animation: 'pulse 1.5s infinite' },
  btnPrimary: { padding: '8px 14px', background: '#2d6a4f', color: '#fff',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' },
  btnOutline: { padding: '8px 14px', border: '1.5px solid #2d6a4f', background: 'transparent',
    color: '#2d6a4f', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' },
  btnUpgrade: { padding: '8px 14px', background: '#f4a261', color: '#fff',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' },
  btnSmall: { padding: '6px 12px', border: '1.5px solid #2d6a4f', background: 'transparent',
    color: '#2d6a4f', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' },
  btnDanger: { padding: '6px 12px', background: '#e63946', color: '#fff',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px' },
  btnConfirm: { padding: '6px 12px', background: '#f4a261', color: '#fff',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' },
  btnDone: { padding: '6px 12px', background: '#2d6a4f', color: '#fff',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' },
  content: { padding: '20px 16px 40px' },
  heading: { fontSize: '20px', color: '#1a1a2e', margin: '0 0 16px', fontWeight: '700' },
  subHeading: { fontSize: '16px', color: '#1a1a2e', margin: '24px 0 12px', fontWeight: '700' },
  callSection: { background: '#fff3e0', border: '1px solid #f4a261',
    borderRadius: '12px', padding: '14px 16px', marginBottom: '16px' },
  callTitle: { fontSize: '15px', fontWeight: '700', color: '#c17b3a',
    margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: '8px' },
  pendingBadge: { fontSize: '11px', background: '#f4a261', color: '#fff',
    padding: '2px 8px', borderRadius: '10px', fontWeight: '400' },
  callCard: { display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: '#fff', borderRadius: '10px', padding: '12px 14px',
    marginBottom: '8px', gap: '12px', flexWrap: 'wrap' },
  callInfo: { flex: 1 },
  callTable: { fontSize: '15px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 2px' },
  callStatus: { fontSize: '12px', color: '#999', margin: 0 },
  callBtns: { display: 'flex', gap: '8px' },
  planBox: { background: '#fff', borderRadius: '12px', padding: '16px',
    marginBottom: '16px', border: '2px solid #2d6a4f' },
  planTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  planLeft: { display: 'flex', alignItems: 'center', gap: '8px' },
  planBadge: { color: '#fff', fontSize: '11px', padding: '3px 10px',
    borderRadius: '20px', whiteSpace: 'nowrap' },
  planPrice: { fontSize: '13px', color: '#666' },
  planGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' },
  planItem: { background: '#f8f9fa', borderRadius: '8px', padding: '8px 10px',
    display: 'flex', flexDirection: 'column', gap: '2px' },
  planLabel: { fontSize: '11px', color: '#999' },
  planVal: { fontSize: '13px', color: '#1a1a2e', fontWeight: '500' },
  upgradeHint: { background: '#fff8f0', border: '1px solid #f4a261',
    borderRadius: '8px', padding: '10px 12px', fontSize: '12px',
    color: '#c17b3a', margin: 0, lineHeight: '1.5' },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '8px' },
  card: { background: '#fff', padding: '14px 12px', borderRadius: '12px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', textAlign: 'center' },
  cardLabel: { color: '#999', fontSize: '11px', margin: '0 0 4px' },
  cardValue: { color: '#2d6a4f', fontSize: '26px', fontWeight: '700', margin: 0 },
  cardUnit: { fontSize: '13px', fontWeight: '400' },
  loadingText: { color: '#999', textAlign: 'center', fontSize: '14px' },
  ranking: { background: '#fff', borderRadius: '12px', overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  rankItem: { display: 'flex', alignItems: 'center', padding: '12px 16px',
    borderBottom: '1px solid #f5f5f5', gap: '10px' },
  rankNum: { width: '24px', height: '24px', borderRadius: '50%', color: '#fff',
    fontSize: '12px', fontWeight: '700', display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  rankName: { flex: 1, fontSize: '14px', color: '#333' },
  rankCount: { fontSize: '13px', color: '#999' },
  empty: { color: '#bbb', padding: '20px', textAlign: 'center', fontSize: '13px' },
  settingBox: { background: '#fff', borderRadius: '12px', padding: '16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '8px' },
  profileRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  logoWrap: { flexShrink: 0 },
  logoImg: { width: '56px', height: '56px', borderRadius: '50%',
    objectFit: 'cover', border: '2px solid #d8f3dc' },
  logoPlaceholder: { width: '56px', height: '56px', borderRadius: '50%',
    background: '#2d6a4f', display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexShrink: 0 },
  logoPlaceholderText: { color: '#fff', fontSize: '22px', fontWeight: '700' },
  profileInfo: { flex: 1, minWidth: 0 },
  profileName: { fontSize: '15px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 2px',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  profileEmail: { fontSize: '12px', color: '#999', margin: 0,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  profileEditor: { marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee',
    display: 'flex', flexDirection: 'column', gap: '16px' },
  logoEditSection: { display: 'flex', flexDirection: 'column', gap: '8px' },
  editorLabel: { fontSize: '13px', color: '#555', margin: 0, fontWeight: '500' },
  logoEditRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  logoPreview: { width: '64px', height: '64px', borderRadius: '50%',
    objectFit: 'cover', border: '2px solid #d8f3dc', flexShrink: 0 },
  logoPlaceholderSm: { width: '64px', height: '64px', borderRadius: '50%',
    background: '#2d6a4f', display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: '#fff', fontSize: '24px',
    fontWeight: '700', flexShrink: 0 },
  logoEditBtns: { display: 'flex', flexDirection: 'column', gap: '6px' },
  hint: { fontSize: '11px', color: '#bbb', margin: 0 },
  nameEditSection: { display: 'flex', flexDirection: 'column', gap: '8px' },
  input: { width: '100%', padding: '10px', border: '1px solid #ddd',
    borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' },
  settingRow: { display: 'flex', flexDirection: 'column', gap: '8px' },
  settingBtns: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  settingLabel: { color: '#999', fontSize: '12px', margin: '0 0 4px' },
  settingValue: { color: '#1a1a2e', fontSize: '13px', margin: 0,
    wordBreak: 'break-all', lineHeight: '1.4' },
  idEditor: { marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #eee' },
  warning: { color: '#c00', fontSize: '12px', marginBottom: '10px',
    background: '#fee', padding: '8px 10px', borderRadius: '8px' },
  idRow: { display: 'flex', gap: '8px' },
};

export default Dashboard;
